import ApiService from '@/apis/apiService'

export interface SSECallbacks {
  onConversationId: (id: string) => void
  onChunk: (text: string) => void
  onDone: () => void
  onError: (error: Error) => void
}

export interface CableStreamHandle {
  conversationId: string
  userMessageId: string
}

function processLine(line: string, callbacks: SSECallbacks): boolean {
  if (!line.startsWith('data: ')) return false
  const data = line.slice(6)

  if (data === '[DONE]') {
    callbacks.onDone()
    return true
  }

  try {
    const parsed = JSON.parse(data)
    if (typeof parsed === 'object' && parsed.conversation_id) {
      callbacks.onConversationId(String(parsed.conversation_id))
      return false
    }
    if (typeof parsed === 'object' && parsed.error) {
      callbacks.onError(new Error(parsed.error))
      return true
    }
  } catch {
    // Not a JSON object — fall through to text chunk handling
  }

  try {
    const text = JSON.parse(data) as string
    callbacks.onChunk(text)
  } catch {
    callbacks.onChunk(data)
  }
  return false
}

async function readStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  callbacks: SSECallbacks,
) {
  const decoder = new TextDecoder()
  let buffer = ''

  const processChunk = async (): Promise<void> => {
    const { done, value } = await reader.read()
    if (done) {
      callbacks.onDone()
      return
    }

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    const isDone = lines.some((line) => processLine(line, callbacks))
    if (!isDone) {
      await processChunk()
    }
  }

  await processChunk()
}

const stream = async (
  question: string,
  conversationId: string | null,
  signal: AbortSignal,
  callbacks: SSECallbacks,
) => {
  const body: Record<string, unknown> = { question }
  if (conversationId) body.conversation_id = conversationId

  const response = await fetch('/api/answers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
    signal,
  })

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`)
  }

  try {
    await readStream(response.body!.getReader(), callbacks)
  } catch (err) {
    if ((err as Error).name !== 'AbortError') {
      callbacks.onError(err as Error)
    }
  }
}

const cable = async (
  question: string,
  conversationId: string | null,
): Promise<CableStreamHandle> => {
  const body: Record<string, unknown> = { question }
  if (conversationId) body.conversationId = conversationId

  const response = await ApiService.post<CableStreamHandle>('/api/answers/cable', body)
  return response.data
}

const cancelCable = async (userMessageId: string): Promise<void> => {
  await ApiService.delete(`/api/answers/cable/${userMessageId}`)
}

export default { stream, cable, cancelCable }
